/**
 * `application/problem+json` — o único formato de erro da API (§6.1, RFC 9457).
 *
 * ── Por que um catálogo, e não código inventado no controller ─────────────
 *
 * `code` é **contrato**: o cliente decide comportamento por ele. Código que
 * nasce dentro de um controller é código que nenhum cliente trata, porque
 * ninguém soube que ele existe. E código que muda depois quebra quem tratava.
 *
 * A regra que sustenta isso é simples e dura: **código existente não muda de
 * significado, de status nem de `retryable`**. Acrescentar é livre; alterar é
 * quebra de contrato e exige rótulo `breaking-change` e ADR (§6, §17.2).
 * `problema.test.mts` trava a tabela inteira para que alterar exija editar o
 * teste — o que transforma um descuido numa decisão.
 */

import { z } from 'zod';

/** Base dos `type`. URI é identificador, não endereço que precise resolver. */
const BASE_DO_TIPO = 'https://api.cliqcrm.com.br/problemas';

export const ProblemaSchema = z
  .object({
    /** URI que identifica a classe do problema. Estável junto com o `code`. */
    type: z.string(),
    /** Resumo legível e **seguro**. Nunca varia com a instância do erro. */
    title: z.string(),
    status: z.number().int(),
    /**
     * Código estável para máquina. É por ele que o cliente decide, e não pelo
     * `title` — texto muda com revisão de português.
     */
    code: z.string(),
    /**
     * Detalhe **seguro** desta ocorrência. Sem stack trace, sem nome de tabela,
     * sem mensagem de upstream. O detalhe de verdade vai para o log, ligado
     * por `correlation_id`.
     */
    detail: z.string().optional(),
    /** Sempre presente. Liga o erro na tela ao log do servidor. */
    correlation_id: z.string(),
    /**
     * Repetir faz sentido? Falha de validação **nunca** é retryable — repetir
     * o mesmo corpo dá o mesmo erro, e cliente que tenta de novo vira carga.
     */
    retryable: z.boolean(),
    /** Um item por campo recusado. Só em erro de validação. */
    errors: z.array(z.object({ campo: z.string(), problema: z.string() })).optional(),
    /**
     * Extensão da RFC 9457 para a recusa que TEM saída (B17).
     *
     * Presente só quando o `code` é `SEM_PERMISSAO` e a permissão volta com
     * reautenticação. Sem ela, a tela diria "sem permissão" para quem tem
     * permissão — bastava confirmar a identidade. `descricao` e `porque` viajam
     * junto porque a H13 exige explicar QUAL ação e POR QUE, e manter uma cópia
     * do catálogo no cliente é como as duas versões divergem.
     *
     * Não é um código novo: a B34 congelou o catálogo em catorze, e a própria
     * RFC prevê membro de extensão exatamente para este caso.
     */
    step_up: z
      .object({
        acao: z.string(),
        descricao: z.string(),
        porque: z.string(),
        exige_mfa: z.boolean(),
        /**
         * A ação pede a justificativa escrita?
         *
         * A tela usa isto para ESCONDER o campo, e não só para não validar:
         * uma caixa de texto opcional numa confirmação de identidade faz a
         * pessoa parar para pensar se deve escrever algo, que é o atrito que
         * a ação sem justificativa existe para tirar.
         *
         * OPCIONAL no schema: um servidor mais velho não manda o campo, e
         * exigi-lo faria a resposta dele deixar de validar. A tela já lê com
         * `?? true` pelo mesmo motivo, e o padrão é PEDIR.
         */
        exige_justificativa: z.boolean().optional(),
        /** Por que ESTA tentativa foi recusada: token velho, sem MFA, sem concessão. */
        motivo: z.string(),
      })
      .optional(),
    /**
     * Extensão para o token VÁLIDO sem vínculo ativo (as telas 7 e 8 do login).
     *
     * `inexistente`: a identidade foi confirmada, mas ninguém a vinculou a esta
     * revenda. `desativado`: já teve vínculo, e ele foi suspenso — ou a conta
     * foi bloqueada ou desativada. São desfechos diferentes na tela: juntá-los
     * faria alguém desligado achar que é erro de sistema, e alguém novo achar
     * que foi punido.
     *
     * Só sai quando a assinatura do token CONFERIU: quem sonda com token
     * inválido continua recebendo o 401 nu, sem saber se a pessoa existe. O
     * código continua `NAO_AUTENTICADO` — membro de extensão, não código novo.
     *
     * Sem data: o banco não guarda quando o vínculo mudou de estado.
     */
    vinculo: z
      .object({
        situacao: z.enum(['inexistente', 'desativado']),
      })
      .optional(),
    /**
     * Extensão para a recusa pelo limite de usuários contratados (#768).
     *
     * Sai no `CONFIRMACAO_EXIGIDA` do convite e da reativação no limite, quando
     * quem pede pode contratar 1 usuário adicional: o mesmo pedido, mais
     * `contratarUsuarioAdicional` com o valor de `usuario_adicional`, é a
     * segunda tentativa. E sai no `CONFLITO_DE_ESTADO` quando não dá para
     * contratar. `motivo` diz qual dos casos: `confirmacao`, `valor_mudou`,
     * `sem_preco`, `nao_contrata` ou `acima`. A tela monta o texto com os
     * números daqui, sem uma cópia das regras.
     *
     * Não é um código novo: a B34 congelou o catálogo, e a RFC prevê membro de
     * extensão para isto.
     */
    limite: z
      .object({
        contratados: z.number().int(),
        em_uso: z.number().int(),
        /** O valor do usuário adicional deste cliente; nulo quando o contrato não o tem. */
        usuario_adicional: z
          .object({ quantia_micros: z.number().int(), moeda: z.string() })
          .strict()
          .nullable(),
        /** Quem pediu pode contratar 1 usuário adicional agora, confirmando o valor. */
        pode_contratar: z.boolean(),
        motivo: z.enum(['confirmacao', 'valor_mudou', 'sem_preco', 'nao_contrata', 'acima']),
      })
      .optional(),
    /**
     * Extensão para a recusa pela cota de armazenamento (#717).
     *
     * Sai no `CONFLITO_DE_ESTADO` do envio de documento (da oportunidade e do
     * paciente) e do anexo que a equipe manda no Atendimento, quando o envio
     * faria a organização passar do armazenamento contratado. Os números em
     * bytes, com 1 GB = `BYTES_POR_GB`; a tela monta o texto com eles.
     *
     * Não é um código novo: a B34 congelou o catálogo.
     */
    armazenamento: z
      .object({
        contratado_gb: z.number().int(),
        em_uso_bytes: z.number().int(),
        /** O tamanho do que este envio guardaria. */
        envio_bytes: z.number().int(),
      })
      .strict()
      .optional(),
    /**
     * Extensão para a recusa de mais um bloco de armazenamento (#718).
     *
     * Sai no `CONFIRMACAO_EXIGIDA` quando falta confirmar o valor do bloco, ou
     * quando ele mudou: o mesmo pedido, com `valor_aceito` igual a
     * `valor_do_bloco`, é a segunda tentativa. E sai no `CONFLITO_DE_ESTADO`
     * quando não dá para contratar. `motivo` diz qual dos casos:
     * `confirmacao`, `valor_mudou`, `sem_preco` ou `nao_contrata`.
     *
     * Não é um código novo: a B34 congelou o catálogo.
     */
    armazenamento_adicional: z
      .object({
        contratado_gb: z.number().int(),
        bloco_gb: z.number().int(),
        /** O valor do bloco deste cliente; nulo quando o contrato não tem o do GB adicional. */
        valor_do_bloco: z
          .object({ quantia_micros: z.number().int(), moeda: z.string() })
          .strict()
          .nullable(),
        /** Quem pediu pode contratar o bloco agora, confirmando o valor. */
        pode_contratar: z.boolean(),
        motivo: z.enum(['confirmacao', 'valor_mudou', 'sem_preco', 'nao_contrata']),
      })
      .strict()
      .optional(),
  })
  .describe('Erro no formato application/problem+json (RFC 9457)');

export type Problema = z.infer<typeof ProblemaSchema>;

interface DefinicaoDeProblema {
  readonly status: number;
  readonly title: string;
  readonly retryable: boolean;
}

/**
 * O catálogo. Uma entrada por classe de erro que a API sabe produzir.
 *
 * Os códigos são em português, como o resto do vocabulário do projeto — o mesmo
 * nome no banco, na especificação e aqui. `title` é o texto que vai para o
 * cliente, e por isso não carrega nada de interno.
 */
export const CATALOGO_DE_PROBLEMAS = {
  NAO_AUTENTICADO: {
    status: 401,
    title: 'Token ausente, inválido ou sem vínculo ativo',
    retryable: false,
  },
  SEM_PERMISSAO: {
    status: 403,
    title: 'Sem permissão para esta operação',
    retryable: false,
  },
  /**
   * Também é a resposta para recurso **de outro tenant**. A §19 exige 404 e não
   * 403: responder 403 confirmaria que o recurso existe, o que permite enumerar
   * identificadores entre revendas.
   */
  NAO_ENCONTRADO: {
    status: 404,
    title: 'Recurso não encontrado',
    retryable: false,
  },
  REQUISICAO_INVALIDA: {
    status: 400,
    title: 'Requisição inválida',
    retryable: false,
  },
  CURSOR_INVALIDO: {
    status: 400,
    title: 'Cursor de paginação inválido ou expirado',
    retryable: false,
  },
  /** `If-Match` ausente em operação que exige a versão atual. */
  VERSAO_EXIGIDA: {
    status: 428,
    title: 'Informe a versão atual do recurso em If-Match',
    retryable: false,
  },
  /** Alguém alterou o recurso entre a leitura e a escrita. */
  CONFLITO_DE_VERSAO: {
    status: 409,
    title: 'O recurso mudou desde a sua leitura',
    retryable: false,
  },
  /**
   * O pedido é válido, e o estado atual não o comporta: nome de unidade que
   * já existe, e-mail que já tem vínculo ativo na revenda (D12).
   *
   * Separado de `CONFLITO_DE_VERSAO` porque a saída é outra. Lá, quem chama
   * relê e tenta de novo com a versão certa; aqui, tentar de novo com o mesmo
   * corpo dá o mesmo 409 para sempre — o que precisa mudar é o corpo.
   */
  CONFLITO_DE_ESTADO: {
    status: 409,
    title: 'O estado atual não comporta este pedido',
    retryable: false,
  },
  /**
   * O pedido é válido e faria o que pede — só que a consequência não é óbvia,
   * e quem pediu precisa dizer que sabe.
   *
   * ── Por que não é `CONFLITO_DE_ESTADO` ────────────────────────────────
   *
   * Lá, tentar de novo com o mesmo corpo dá o mesmo 409 para sempre: o que
   * precisa mudar é o corpo. Aqui é o contrário — o mesmo corpo, mais o campo
   * de confirmação, é exatamente o que se espera na segunda tentativa.
   *
   * O primeiro caso é convidar um e-mail que já tem conta em OUTRA revenda: a
   * mesma conta passa a entrar também nesta, e quem convida confirma antes
   * que é mesmo aquela pessoa, e não um e-mail digitado errado.
   */
  CONFIRMACAO_EXIGIDA: {
    status: 409,
    title: 'Este pedido precisa de confirmação',
    retryable: false,
  },
  /** Comando com efeito externo sem o cabeçalho `Idempotency-Key` (§6.1). */
  IDEMPOTENCIA_EXIGIDA: {
    status: 400,
    title: 'Este comando exige Idempotency-Key',
    retryable: false,
  },
  /** Mesma chave de idempotência com corpo diferente (§6.1). B30. */
  IDEMPOTENCIA_DIVERGENTE: {
    status: 409,
    title: 'Idempotency-Key já usada com outro corpo',
    retryable: false,
  },
  /** Mesma chave, ainda em execução. Repetir depois faz sentido. */
  IDEMPOTENCIA_EM_CURSO: {
    status: 409,
    title: 'Requisição com esta Idempotency-Key ainda em processamento',
    retryable: true,
  },
  /**
   * Envio fora da janela de atendimento do canal, sem template aprovado
   * (§8.2, C22). Erro ESPECÍFICO de propósito: sem ele, o envio sairia,
   * falharia no provedor e a mensagem sumiria sem explicação clara — a
   * Técnica da C22 manda verificar antes e dizer o que fazer.
   */
  FORA_DA_JANELA: {
    status: 422,
    title: 'Fora da janela de atendimento — este canal exige template aprovado',
    retryable: false,
  },
  LIMITE_EXCEDIDO: {
    status: 429,
    title: 'Limite de requisições excedido',
    retryable: true,
  },
  ERRO_INTERNO: {
    status: 500,
    title: 'Erro interno',
    retryable: false,
  },
  /** Engine ou portal fora do ar. Retryable — é falha transitória de terceiro. */
  ENGINE_INDISPONIVEL: {
    status: 503,
    title: 'Serviço temporariamente indisponível',
    retryable: true,
  },
  /**
   * O banco do cliente (o sistema dele, lido pelo serviço de automações) não
   * respondeu: conexão, autenticação ou tempo. Passageiro, e não é o CRM nem o
   * serviço que caiu: a tela diz isso, em vez de "tente mais tarde" genérico.
   */
  BANCO_DO_CLIENTE_INDISPONIVEL: {
    status: 503,
    title: 'O sistema do cliente não respondeu',
    retryable: true,
  },
} as const satisfies Record<string, DefinicaoDeProblema>;

export type CodigoDeProblema = keyof typeof CATALOGO_DE_PROBLEMAS;

/** `NAO_ENCONTRADO` → `.../problemas/nao-encontrado`. */
export function tipoDoProblema(codigo: CodigoDeProblema): string {
  return `${BASE_DO_TIPO}/${codigo.toLowerCase().replace(/_/g, '-')}`;
}

/** O que vai além do catálogo: o detalhe, os erros por campo e os membros de extensão. */
export type ExtrasDoProblema = Partial<
  Omit<Problema, 'type' | 'title' | 'status' | 'code' | 'correlation_id' | 'retryable'>
>;

/** Os membros que o catálogo fixa: nenhum extra os troca. */
const DO_CATALOGO = new Set(['type', 'title', 'status', 'code', 'correlation_id', 'retryable']);

/**
 * Os membros que o corpo aceita além do catálogo, tirados do próprio schema, e
 * não de uma lista escrita à mão. Com a lista, `limite` e `armazenamento`
 * chegavam aqui e sumiam sem erro de tipo (espalhar um objeto não passa pela
 * checagem de campo a mais), e a tela nunca os recebia (#718). Pelo schema, o
 * membro novo entra sozinho, e a chave que o contrato não conhece fica de fora.
 */
const MEMBROS_EXTRAS = Object.keys(ProblemaSchema.shape).filter(
  (membro) => !DO_CATALOGO.has(membro),
);

export function montarProblema(
  codigo: CodigoDeProblema,
  correlationId: string,
  extras: ExtrasDoProblema = {},
): Problema {
  const definicao = CATALOGO_DE_PROBLEMAS[codigo];
  // Nenhum membro de primeiro nível aceita nulo: nulo sairia como corpo inválido.
  const definidos = Object.fromEntries(
    MEMBROS_EXTRAS.flatMap((membro) => {
      const valor = (extras as Readonly<Record<string, unknown>>)[membro];
      return valor === undefined || valor === null ? [] : [[membro, valor]];
    }),
  ) as ExtrasDoProblema;
  return {
    ...definidos,
    type: tipoDoProblema(codigo),
    title: definicao.title,
    status: definicao.status,
    code: codigo,
    correlation_id: correlationId,
    retryable: definicao.retryable,
  };
}
